import { Radio } from '@ksyos/design-system';
import { useEffect } from 'react';
import { YesOrNoOrNotApplicable } from '@common/enums';
import {
    BooleanRadioGroupFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { TherapielandProgrammesEdit } from '@frontend/components/pages/episodeCategory/therapielandProgrammes/TherapielandProgrammesEdit';
import { useOsasAllTreatments } from '@frontend/utils/hooks/useOsasAllTreatments';
import type { PolicyFormSectionProps } from './PolicyFormSection';
import { osasNoChosenTreatmentInitialData, osasYesChosenTreatmentInitialData } from '../../osasConsultData';
import { TreatmentFormSection } from '../treatmentFormSection/TreatmentFormSection';
import { HasChosenTreatmentFields } from './HasChosenTreatmentFields';
import { NoChosenTreatmentFields } from './NoChosenTreatmentFields';
import { NotifyGeneralPractitioner } from './NotifyGeneralPractitioner';

export function OsaYesPolicy(props: Readonly<PolicyFormSectionProps>) {
    const { data, errors, onChange } = props;
    const { onChangeTreatment } = useOsasAllTreatments({ onChange });

    useEffect(() => {
        if (data.hasTreatment !== false) {
            onChange(
                {
                    ...osasNoChosenTreatmentInitialData,
                },
                [
                    'callPatientAfterWeeks',
                    'messageToGeneralPractitioner',
                    'shouldNotifyGeneralPractitioner',
                    'remarkNoTreatment',
                ]
            );
        }

        if (data.hasTreatment !== true) {
            onChange(
                {
                    ...osasYesChosenTreatmentInitialData,
                },
                [
                    'allTreatments',
                    'cpapSetting',
                    'cpapSettingText',
                    'refer',
                    'remarkTreatment',
                    'resendQuestionnaires',
                    'resendQuestionnairesWeeks',
                    'shouldHaveFollowUpConsult',
                    'shouldHaveFollowingExamination',
                    'taskFollowingExaminationOrConsultWeeks',
                    'taskForPoliclinic',
                    'treatment',
                    'treatmentFollowUp',
                ]
            );
        }
    }, [data.hasTreatment]);

    return (
        <>
            <BooleanRadioGroupFormGroup
                fieldKey="hasTreatment"
                isRequired={true}
                label="Patiënt heeft behandelkeuze gemaakt"
                type="radio"
                value={data.hasTreatment}
            />

            {data.hasTreatment === true && (
                <>
                    <TreatmentFormSection
                        data={data}
                        errors={errors}
                        onChange={(updatedData, validateKeys) => {
                            onChange(updatedData, validateKeys);
                            onChangeTreatment(updatedData.treatment);
                        }}
                    />

                    <TextAreaFormGroup
                        fieldKey="taskForPoliclinic"
                        label="Taak voor de Ksyos poli"
                    />
                </>
            )}

            {data.hasTreatment === false && <NoChosenTreatmentFields {...props} />}

            <TherapielandProgrammesEdit
                checkedTreatments={data.checkedTreatments}
                errors={errors}
                onChange={onChange}
            />

            <NotifyGeneralPractitioner {...props} />
            {data.hasTreatment === true && <HasChosenTreatmentFields {...props} />}

            <RadioGroupFormGroup
                fieldKey="isDrivingCompetenceDiscussed"
                label="Rijbevoegdheid besproken"
                onChange={async (value) => {
                    onChange(
                        {
                            isDrivingCompetenceDiscussed: value as YesOrNoOrNotApplicable,
                            remarkIsDrivingCompetenceDiscussed: null,
                        },
                        'isDrivingCompetenceDiscussed'
                    );
                }}
                radioGroupLayout={'row'}
                value={data?.isDrivingCompetenceDiscussed ?? undefined}
            >
                <Radio
                    key={YesOrNoOrNotApplicable.yes}
                    label="Ja"
                    value={YesOrNoOrNotApplicable.yes}
                />
                <Radio
                    key={YesOrNoOrNotApplicable.no}
                    label="Nee"
                    value={YesOrNoOrNotApplicable.no}
                />
                <Radio
                    key={YesOrNoOrNotApplicable.notApplicable}
                    label="n.v.t."
                    value={YesOrNoOrNotApplicable.notApplicable}
                />
            </RadioGroupFormGroup>

            {data.isDrivingCompetenceDiscussed === YesOrNoOrNotApplicable.yes && (
                <TextAreaFormGroup
                    fieldKey={'remarkIsDrivingCompetenceDiscussed'}
                    label={'Toelichting rijbevoegdheid'}
                />
            )}

            <TextAreaFormGroup
                fieldKey="remarkOther"
                label="Overige opmerkingen"
                value={data.remarkOther}
            />
        </>
    );
}
